import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
  Image,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import BodyPartsTypeModal from './BodyPartTypeModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import { BPTs } from "../../../../types/BodyPartType";
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));


const BodyPartType: React.FC = () => {
  const [bodyparttypedata, setBodyparttypedata] = useState<BPTs[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<BPTs | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchBs = async () => {
    try {
      const data = await apiRequest<BPTs[]>({
        method: 'get',
        url: 'body-part-type',
      });
      setBodyparttypedata(data);
    } catch (error) {
      showError('Failed to fetch body part type ');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchBs();
  }, []);

  const handleEditClick = (record: BPTs) => {
    setIsEditMode(true);
    setInitialValues({
      bpptm_id: record.bpptm_id,
      bpptm_status: record.bpptm_status,
      bpptm_name: record.bpptm_name,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchBs();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (bpptm_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-body-part-type/${bpptm_id}`,
      });
      showSuccess('Body Part type deleted');
      fetchBs();
    } catch {
      showError('Failed to delete body part type');
    }
  };

  const handleStatusToggle = async (bsId: number) => {
    setBodyparttypedata((prev) =>
      prev.map((item) =>
        item.bpptm_id === bsId
          ? { ...item, bpptm_status: item.bpptm_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-body-part-type/${bsId}`,
      });
      showSuccess('Body part type status updated');
    } catch (error) {
      showError('Failed to update status');
      setBodyparttypedata((prev) =>
        prev.map((item) =>
          item.bpptm_id === bsId
            ? { ...item, bpptm_status: item.bpptm_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<BPTs>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<BPTs> = [
    {
      title: 'Body Part Type Name',
      dataIndex: 'bpptm_name',
      key: 'bpptm_name',
      width: 200,
      filters: Array.from(new Set(bodyparttypedata.map((s) => s.bpptm_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.bpptm_name || null,
      // onFilter: (value, record) => record.bpptm_name.includes(value as string),
      // sorter: (a, b) => a.bpptm_name.localeCompare(b.bpptm_name),
      sortOrder: sortedInfo.columnKey === 'bpptm_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.bpptm_name}</div>,
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: BPTs) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.bpptm_status}
                onChange={() => handleStatusToggle(record.bpptm_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this bs?"
              onConfirm={() => handleDelete(record.bpptm_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Body Part Type </Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Body Part Type
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<BPTs>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="bpptm_id"
          dataSource={bodyparttypedata.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No Body Part type  found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, bodyparttypedata.length)} of{' '}
                {bodyparttypedata.length}
              </span>
              <Pagination
                {...pagination}
                total={bodyparttypedata.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <BodyPartsTypeModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        bodyparttypedata={bodyparttypedata}
      />
    </>
  );
};

export default BodyPartType;